/**
 * GESTOR DE INTERFAZ DE USUARIO
 * Maneja elementos de UI: botones de calibración, mensajes de error/éxito
 */

import { UI_CONFIG } from '@config/constants';
import { safeQuerySelector } from '@utils/domUtils';
import { HitboxSystem } from './HitboxSystem';
import type { UIState, DOMElements, CSSStyles } from '@types';

export class UIManager {
  private hitboxSystem: HitboxSystem | null;
  private elements: DOMElements = {};

  constructor(hitboxSystem?: HitboxSystem) {
    this.hitboxSystem = hitboxSystem || null;
    
    this.initializeElements();
    this.setupEventListeners();
  }

  /**
   * Inicializa referencias a elementos UI
   */
  private initializeElements(): void {
    const selectors = {
      calibrationToggle: '#calibrationToggle'
    };

    for (const [name, selector] of Object.entries(selectors)) {
      this.elements[name] = safeQuerySelector(selector);
      
      if (!this.elements[name]) {
        console.warn(`UIManager: Elemento no encontrado - ${name}`);
      }
    }
  }

  /**
   * Configura event listeners
   */
  private setupEventListeners(): void {
    // Botón de calibración
    const calibrationButton = this.elements.calibrationToggle;
    if (calibrationButton) {
      calibrationButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.toggleCalibration();
      });
    }
  }

  /**
   * Toggle de calibración
   */
  toggleCalibration(): void {
    if (this.hitboxSystem) {
      this.hitboxSystem.toggleCalibration();
      this.updateCalibrationButton();
    } else {
      console.warn('HitboxSystem no disponible para calibración');
    }
  }

  /**
   * Actualiza el estado del botón de calibración
   */
  private updateCalibrationButton(): void {
    const button = this.elements.calibrationToggle;
    if (button && this.hitboxSystem) {
      const isActive = this.hitboxSystem.showCalibration;
      button.textContent = isActive ? 'Ocultar Calibración' : 'Mostrar Calibración';
      button.classList.toggle('active', isActive);
    }
  }

  /**
   *  Muestra mensaje de error
   */
  showError(message: string, duration: number = UI_CONFIG.ERROR_DISPLAY_TIME || 3000): void {
    // Crear elemento de error si no existe
    let errorElement = safeQuerySelector('#errorMessage');
    
    if (!errorElement) {
      errorElement = this.createErrorElement();
      document.body.appendChild(errorElement);
    }

    // Mostrar mensaje
    errorElement.textContent = message;
    errorElement.style.display = 'block';

    // Auto-ocultar después del tiempo especificado
    setTimeout(() => {
      if (errorElement) {
        errorElement.style.display = 'none';
      }
    }, duration);
  }

  /**
   * Crea elemento de error
   */
  private createErrorElement(): HTMLElement {
    const errorElement = document.createElement('div');
    errorElement.id = 'errorMessage';
    
    const styles: CSSStyles = {
      position: 'fixed',
      top: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: 'rgba(255, 0, 0, 0.9)',
      color: 'white',
      padding: '15px 25px',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: 'bold',
      zIndex: '10000',
      display: 'none',
      maxWidth: '80%',
      textAlign: 'center',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
    };

    Object.assign(errorElement.style, styles);
    
    return errorElement;
  }

  /**
   * Muestra mensaje de éxito
   */
  showSuccess(message: string, duration: number = 2000): void {
    // Crear elemento de éxito si no existe
    let successElement = safeQuerySelector('#successMessage');
    
    if (!successElement) {
      successElement = this.createSuccessElement();
      document.body.appendChild(successElement);
    }

    // Mostrar mensaje
    successElement.textContent = message;
    successElement.style.display = 'block';

    // Auto-ocultar después del tiempo especificado
    setTimeout(() => {
      if (successElement) {
        successElement.style.display = 'none';
      }
    }, duration);
  }

  /**
   * Crea elemento de éxito
   */
  private createSuccessElement(): HTMLElement {
    const successElement = document.createElement('div');
    successElement.id = 'successMessage';
    
    const styles: CSSStyles = {
      position: 'fixed',
      top: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: 'rgba(0, 255, 0, 0.9)',
      color: 'white',
      padding: '15px 25px',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: 'bold',
      zIndex: '10000',
      display: 'none',
      maxWidth: '80%',
      textAlign: 'center',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
    };

    Object.assign(successElement.style, styles);
    
    return successElement;
  }

  /**
   * Obtiene estado actual de la UI
   */
  getUIState(): UIState {
    return {
      calibrationActive: this.hitboxSystem?.showCalibration || false,
      errorsVisible: !!safeQuerySelector('#errorMessage[style*="block"]')
    };
  }

  /**
   * Reinicia la UI al estado inicial
   */
  reset(): void {
    console.log('Reiniciando UI...');
    
    // Ocultar mensajes de error/éxito
    const errorElement = safeQuerySelector('#errorMessage');
    const successElement = safeQuerySelector('#successMessage');
    
    if (errorElement) errorElement.style.display = 'none';
    if (successElement) successElement.style.display = 'none';
    
    // Resetear calibración
    if (this.hitboxSystem && this.hitboxSystem.showCalibration) {
      this.hitboxSystem.toggleCalibration(false);
      this.updateCalibrationButton();
    }
    
    console.log('UI reiniciada');
  }

  /**
   * Limpia recursos de la UI
   */
  cleanup(): void {
    console.log('Limpiando recursos de UIManager...');
    
    // Remover elementos creados dinámicamente
    const errorElement = safeQuerySelector('#errorMessage');
    const successElement = safeQuerySelector('#successMessage');
    
    if (errorElement) errorElement.remove();
    if (successElement) successElement.remove();
    
    console.log('UIManager limpiado');
  }
}